import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HardHat, Wrench, LifeBuoy, Plus, List, AlertTriangle, Clock, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCachedData } from "../lib/useCachedData";
import { fetchAndCache, readCacheEntry } from "../lib/dataCache";
import { CATEGORIES, welcomeMessage } from "../lib/categories";

// Match the TTL used by useCachedData so the SPA bounce home → list → home
// → list stays truly zero-call within 30 seconds. Mutations invalidate via
// the axios interceptor, so staleness never leaves outdated data on screen.
const PREFETCH_STALE_MS = 30_000;

// Match the scaffolding icon used in the navbar for visual consistency.
const ScaffoldIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect x="5" y="3" width="14" height="18" rx="0.5" />
    <line x1="5" y1="12" x2="19" y2="12" />
    <line x1="5" y1="3" x2="19" y2="12" />
    <line x1="19" y1="3" x2="5" y2="12" />
    <line x1="5" y1="12" x2="19" y2="21" />
    <line x1="19" y1="12" x2="5" y2="21" />
  </svg>
);

const ICONS = {
  scaffold: ScaffoldIcon,
  hardhat: HardHat,
  tool: Wrench,
  lifebuoy: LifeBuoy,
};

function CategoryTile({ cat, data, isAdmin }) {
  const Icon = ICONS[cat.icon] || HardHat;
  const navigate = useNavigate();
  const expired = data?.expired || 0;
  const expiring = data?.expiring || 0;
  const alerts = expired + expiring;
  const total = data?.total || 0;

  const go = (e, path) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(path);
  };

  const badge = alerts > 0 ? (
    <div
      className={`absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full shadow-sm flex items-center justify-center text-[11px] font-bold ${
        expired > 0 ? "bg-[#FF3B30] text-white" : "bg-[#FFCC00] text-black"
      }`}
      data-testid={`cat-badge-${cat.key}`}
      title={`${expired} scaduti · ${expiring} in scadenza`}
    >
      {alerts > 99 ? "99+" : alerts}
    </div>
  ) : null;

  const inner = (
    <div className="h-full flex flex-col justify-between">
      <div className="flex items-start justify-between gap-3">
        <div className="relative">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${cat.accent}`}>
            <Icon className="w-7 h-7" />
          </div>
          {badge}
        </div>
        <div className="text-right">
          <div className="tag-label">Totale</div>
          <div className="text-3xl font-bold tabular-nums">{total}</div>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xl font-bold tracking-tight">{cat.label}</div>
        <div className="text-xs text-neutral-500 mt-0.5">
          {expired > 0 && <span className="text-[#FF3B30] font-semibold">{expired} scaduti</span>}
          {expired > 0 && expiring > 0 && <span> · </span>}
          {expiring > 0 && <span className="text-[#FFA000] font-semibold">{expiring} in scadenza</span>}
          {alerts === 0 && <span>Tutto in regola</span>}
        </div>
      </div>
      {isAdmin ? (
        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => go(e, cat.hubPath)}
            className="bru-btn bru-btn-secondary flex-1 justify-center"
            data-testid={`home-list-${cat.key}`}
          >
            <List className="w-4 h-4" />
            Lista
          </button>
          <button
            type="button"
            onClick={(e) => go(e, `${cat.hubPath}?new=1`)}
            className="bru-btn bru-btn-primary flex-1 justify-center"
            data-testid={`home-add-${cat.key}`}
          >
            <Plus className="w-4 h-4" />
            Aggiungi
          </button>
        </div>
      ) : (
        <div className="mt-5 flex items-center justify-end text-sm font-semibold text-neutral-500">
          Apri <ChevronRight className="w-4 h-4" />
        </div>
      )}
    </div>
  );

  // The entire tile is clickable and routes to the category list page (admin
  // and client alike). The two inner buttons stop propagation so admins can
  // still choose explicitly between "Lista" and "+ Aggiungi".
  const className =
    "relative bru-card p-5 md:p-6 h-full block cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg";

  return (
    <Link to={cat.hubPath} className={className} data-testid={`cat-tile-${cat.key}`}>
      {inner}
    </Link>
  );
}

export default function HomeDashboard() {
  const { isAdmin } = useAuth();
  const { data, loading } = useCachedData(
    "dashboard:home",
    () => api.get("/dashboard/home").then((r) => r.data),
  );
  const greeting = useMemo(() => welcomeMessage(new Date()), []);

  // Prefetch asset lists and clients so clicking a tile opens instantly.
  // Fired once per session mount; each key hits the server at most once per
  // STALE window thanks to the readCacheEntry freshness check (mirrors the
  // TTL baked into useCachedData).
  useEffect(() => {
    const prefetch = [
      ["devices:dpi",         () => api.get("/devices?kind=dpi").then((r) => r.data || [])],
      ["devices:strumento",   () => api.get("/devices?kind=strumento").then((r) => r.data || [])],
      ["scaffolds:scaffalatura", () => api.get("/scaffolds?kind=scaffalatura").then((r) => r.data || [])],
      ["scaffolds:lineavita", () => api.get("/scaffolds?kind=lineavita").then((r) => r.data || [])],
    ];
    if (isAdmin) {
      prefetch.push(["clients:all", () => api.get("/clients").then((r) => r.data || []).catch(() => [])]);
    }
    const now = Date.now();
    for (const [key, fetcher] of prefetch) {
      const e = readCacheEntry(key);
      if (e && e.ts && now - e.ts < PREFETCH_STALE_MS) continue; // still fresh
      fetchAndCache(key, fetcher).catch(() => {});
    }
  }, [isAdmin]);

  const upcoming = data?.upcoming_30d || [];
  const summary = data?.summary || {};

  return (
    <main className="min-h-screen px-4 sm:px-6 lg:px-10 py-6 sm:py-10 max-w-7xl mx-auto">
      {/* Welcome header */}
      <section className="mb-6 sm:mb-8" data-testid="home-welcome">
        <div className="tag-label text-[#ff5a00]">
          {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-1 leading-tight">
          {greeting}
        </h1>
      </section>

      {/* 2x2 Grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5" data-testid="home-grid">
        {CATEGORIES.map((cat) => (
          <CategoryTile
            key={cat.key}
            cat={cat}
            data={data?.categories?.[cat.key]}
            isAdmin={isAdmin}
          />
        ))}
      </section>

      {/* 30-day expiries widget */}
      <section className="mt-6 sm:mt-8">
        <Link
          to="/scadenze"
          className={`block bru-card p-5 sm:p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg ${
            upcoming.length > 0 ? "border-[#FFCC00] bg-[#FFF9E6]" : ""
          }`}
          data-testid="home-widget-30d"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                upcoming.length > 0 ? "bg-[#FFCC00] text-black" : "bg-neutral-900 text-white"
              }`}>
                {upcoming.length > 0 ? <AlertTriangle className="w-6 h-6" /> : <Clock className="w-6 h-6" />}
              </div>
              <div>
                <div className="tag-label">Prossimi 30 giorni</div>
                <div className="text-xl sm:text-2xl font-bold mt-0.5" data-testid="home-widget-30d-count">
                  {loading ? "…" : (
                    upcoming.length > 0
                      ? `⚠️ ${upcoming.length} ${upcoming.length === 1 ? "elemento" : "elementi"} in scadenza`
                      : "Nessuna scadenza imminente"
                  )}
                </div>
                {summary.total_expired > 0 && (
                  <div className="text-sm text-[#FF3B30] font-semibold mt-1">
                    di cui {summary.total_expired} già scaduti
                  </div>
                )}
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-neutral-400" />
          </div>
        </Link>
      </section>
    </main>
  );
}
